import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, Typography, message } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';
import { doctorQrJsonString } from '../utils/doctorQr';

const { Text, Paragraph } = Typography;

type Props = {
  doctorPublicId: string;
  clinicName?: string | null;
  clinicCity?: string | null;
  deepLink?: string | null;
  compact?: boolean;
};

export const DoctorPublicIdCard: React.FC<Props> = ({
  doctorPublicId,
  clinicName,
  clinicCity,
  deepLink,
  compact = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrReady, setQrReady] = useState(false);
  const jsonPayload = doctorQrJsonString(doctorPublicId);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !doctorPublicId) return;
    let cancelled = false;
    QRCode.toCanvas(canvas, jsonPayload, {
      width: compact ? 160 : 220,
      margin: 2,
      errorCorrectionLevel: 'M',
    })
      .then(() => {
        if (!cancelled) setQrReady(true);
      })
      .catch(() => {
        if (!cancelled) setQrReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doctorPublicId, jsonPayload, compact]);

  const handleDownloadPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `doctor-qr-${doctorPublicId}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonPayload);
      message.success('JSON для QR скопирован');
    } catch {
      message.error('Не удалось скопировать');
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Paragraph copyable={{ text: doctorPublicId }} style={{ marginBottom: 0, fontSize: 16 }}>
        <Text strong>{doctorPublicId}</Text>
      </Paragraph>
      {(clinicName || clinicCity) && (
        <Text type="secondary">
          {clinicName}
          {clinicCity ? `, ${clinicCity}` : ''}
        </Text>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
        <canvas
          ref={canvasRef}
          style={{
            borderRadius: 8,
            border: '1px solid #e8e8e8',
            opacity: qrReady ? 1 : 0.4,
          }}
        />
        <Space direction="vertical" size="small">
          <Text type="secondary">
            Пациентка может отсканировать QR или вставить код в профиле → «Привязка к врачу».
          </Text>
          {deepLink && (
            <Paragraph copyable={{ text: deepLink }} style={{ marginBottom: 0, fontSize: 12 }}>
              <Text type="secondary">Ссылка: {deepLink}</Text>
            </Paragraph>
          )}
          <Space wrap>
            <Button icon={<CopyOutlined />} onClick={copyJson} size="small">
              Копировать JSON QR
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadPng}
              size="small"
              disabled={!qrReady}
            >
              Скачать PNG
            </Button>
          </Space>
        </Space>
      </div>
    </Space>
  );
};
